import React, { type ForwardedRef, forwardRef, useContext, useEffect, useRef } from 'react';
import classnames from 'classnames';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { TableContext } from '@hh.ru/magritte-ui-table/context';
import { TableRowProps } from '@hh.ru/magritte-ui-table/types';

import cellStyles from './cell.less';
import styles from './table.less';

const TableRowComponent = (
    {
        style,
        styleHighlighted,
        action,
        showAction,
        animated,
        onClick,
        panel,
        showPanel,
        children,
        measure,
        align,
        'data-qa': dataQa = 'table-row',
        ...rest
    }: TableRowProps,
    ref: ForwardedRef<HTMLTableRowElement>
) => {
    const { paddingLeft, paddingRight } = useContext(TableContext);
    const highlightedColor = styleHighlighted || style;
    const wasPanelShown = useRef<boolean | null>(null);
    const rowRef = useRef(null);
    const multiRef = useMultipleRefs(ref, rowRef);

    useEffect(() => {
        if (wasPanelShown.current === null) {
            wasPanelShown.current = !!(panel && showPanel);
            return;
        }

        if (wasPanelShown.current !== !!(panel && showPanel)) {
            wasPanelShown.current = !!(panel && showPanel);
            rowRef.current && measure?.(rowRef.current);
        }
    }, [panel, showPanel, measure]);

    return (
        <>
            <tr
                {...rest}
                className={classnames(styles.row, {
                    [styles.active]: !!onClick,
                    [styles.animated]: animated,
                    [styles.withPanel]: showPanel && panel,
                    [styles[`style-${style}`]]: style,
                    [styles[`style-highlighted-${highlightedColor}`]]: highlightedColor,
                })}
                onClick={onClick}
                ref={multiRef}
                data-qa={dataQa}
                style={{
                    textAlign: align,
                }}
            >
                {!!paddingLeft && (
                    <td
                        className={classnames(cellStyles.spaceCell, cellStyles.pinned, styles.td)}
                        data-qa="table-row-left-space"
                        style={{ width: paddingLeft }}
                    >
                        {showAction && action && <div className={cellStyles.control}>{action}</div>}
                    </td>
                )}
                {children}
                {!!paddingRight && (
                    <td
                        className={classnames(cellStyles.spaceCell, styles.td)}
                        data-qa="table-row-right-space"
                        style={{ width: paddingRight }}
                    ></td>
                )}
            </tr>
            {panel && showPanel && (
                <tr
                    data-qa={`${dataQa}-panel`}
                    data-magritte-table-row-panel
                    className={classnames(styles.row, styles.rowPanel, styles.animated)}
                    style={{
                        textAlign: align,
                    }}
                >
                    {!!paddingLeft && (
                        <td
                            className={classnames(cellStyles.spaceCell, cellStyles.pinned, styles.td)}
                            data-qa="table-row-left-space"
                            style={{ width: paddingLeft }}
                        ></td>
                    )}
                    {panel}
                    {!!paddingRight && (
                        <td
                            className={classnames(cellStyles.spaceCell, styles.td)}
                            data-qa="table-row-right-space"
                            style={{ width: paddingRight }}
                        ></td>
                    )}
                </tr>
            )}
        </>
    );
};

export const TableRow = forwardRef(TableRowComponent);
